import React, { useContext, useEffect, useRef, useState } from 'react';
import { AppContext } from '../../App';
import { useIsLabViewer } from '../../hooks/useIsLabViewer';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import { Col, Spinner } from 'react-bootstrap';
import Table from 'react-bootstrap/Table';
import ProductDetailModal from '../Product/ProductDetailModal';
import DeleteButton from '../Generic/DeleteButton';
import {
  deleteManufacturer,
  getManufacturerDetails,
  getManufacturerDetailsLazy,
} from '../../clients/manufacturer_client';
import ManufacturerModal from './ManufacturerModal';
import SupplierDetailModal from '../Supplier/SupplierDetailModal';
import NavigateToUrlIcon from '../Generic/NavigateToUrlIcon';
import { logError } from '../../config_and_helpers/logger';

/**
 * Represents a modal component for displaying manufacturer details.
 * @param {boolean} isSupplier - Flag indicating if the manufacturer is a supplier.
 * @param {object} manufacturerObj - The object containing the manufacturer details.
 * @param {function} handleDelete - The function to handle when the delete button is clicked.
 * @param {string} manufacturerId - The ID of the manufacturer.
 * @param {function} clearSearchValue - The function to clear the search value.
 * @param {boolean} smallerFont - Flag indicating if the font size should be smaller for better presentation on tables.
 * @returns {React.Component} The ManufacturerDetailModal component.
 */
const ManufacturerDetailModal = ({
  isSupplier,
  manufacturerObj,
  handleDelete,
  manufacturerId,
  clearSearchValue,
  smallerFont,
}) => {
  // useContext retrieves the token value that is required for fetching API data
  const { token } = useContext(AppContext);
  const isViewer = useIsLabViewer();
  // useRef creates a reference boolean variable that persists between re-renders; used to track whether data is being fetched
  const isLoadingRef = useRef(false);
  // useState creates a state variable to control the visibility of the modal; initially set to false
  const [show, setShow] = useState(false);
  // useState creates a state variable to store the details of the manufacturer; initially set to the passed manufacturerObj prop
  const [manufacturer, setManufacturer] = useState(manufacturerObj);
  // useState for detailed manufacturer data (products and suppliers)
  const [detailedData, setDetailedData] = useState(null);
  // useState for loading state of detailed data
  const [loadingDetails, setLoadingDetails] = useState(false);
  // useState for error state
  const [error, setError] = useState(null);
  // Checks if a manufacturer object was passed as a prop;
  // if true, use the id from the manufacturer object, otherwise use the manufacturerId prop
  const manufacturerIdToUse = manufacturerObj
    ? manufacturerObj.id
    : manufacturerId;

  // UseEffect to update the state on re-renders
  useEffect(() => {
    setManufacturer(manufacturerObj);
  }, [manufacturerObj]);

  // Function to fetch detailed manufacturer data (products and suppliers)
  const fetchDetailedData = async () => {
    if (!manufacturerIdToUse || detailedData || loadingDetails) {
      return;
    }

    setLoadingDetails(true);
    setError(null);

    try {
      const data = await getManufacturerDetailsLazy(token, manufacturerIdToUse);
      setDetailedData(data);
      setLoadingDetails(false);
    } catch (err) {
      logError('ManufacturerDetailModal', 'fetchDetailedData failed', err);
      setError('Failed to load detailed information');
      setLoadingDetails(false);
    }
  };

  // Function to fetch basic manufacturer details (legacy)
  const fetchManufacturer = () => {
    isLoadingRef.current = true;
    getManufacturerDetails(token, manufacturerIdToUse, setManufacturer).then(
      response => {
        isLoadingRef.current = false;
      }
    );
  };

  // Only fetch on hover if we have manufacturerId but no manufacturer data

  // Function to handle closing the modal
  const handleClose = () => setShow(false);

  // Function to handle showing the modal
  const handleShow = () => {
    setShow(true);
    // Load detailed data when modal opens
    fetchDetailedData();
  };

  // Note: Removed early spinner return to allow modal to show with loading state inside

  return (
    <div>
      {/* Button to open the modal */}
      <Button
        variant="link"
        onClick={handleShow}
        onMouseEnter={() => {
          if (manufacturerIdToUse && !isLoadingRef.current) {
            fetchManufacturer();
          }
        }}
        {...(smallerFont
          ? { style: { fontSize: '12px', padding: 0 } }
          : { style: { padding: 0 } })}
      >
        {manufacturer && manufacturer.name
          ? manufacturer.name
          : `Manufacturer ${manufacturerIdToUse}`}
      </Button>

      {/* Manufacturer detail modal */}
      {manufacturer && (
        <Modal
          show={show}
          onHide={() => setShow(false)}
          aria-labelledby="product-modal"
          size="lg"
        >
          <Modal.Header closeButton>
            {/* Modal title */}
            <Modal.Title>{manufacturer.name}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {/* Loading state for manufacturer data */}
            {isLoadingRef.current && (
              <div className="text-center">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">
                    Loading manufacturer...
                  </span>
                </Spinner>
                <p className="mt-2">Loading manufacturer data...</p>
              </div>
            )}

            {manufacturer && !isLoadingRef.current && (
              <Container>
                {/* Display website */}
                <Row>
                  <Col>
                    <p className="fs-6 fw-bold">Website:</p>
                  </Col>
                  <Col>
                    {/* Website link */}
                    <a
                      href={manufacturer.website}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <NavigateToUrlIcon />
                    </a>
                  </Col>
                </Row>

                {/* Error display */}
                {error && (
                  <Row>
                    <Col>
                      <div className="alert alert-danger" role="alert">
                        {error}
                        <button
                          className="btn btn-sm btn-outline-danger ms-2"
                          onClick={fetchDetailedData}
                        >
                          Retry
                        </button>
                      </div>
                    </Col>
                  </Row>
                )}

                {/* Loading state for detailed data */}
                {loadingDetails && (
                  <Row>
                    <Col className="text-center">
                      <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </Spinner>
                      <p className="mt-2">Loading detailed information...</p>
                    </Col>
                  </Row>
                )}

                {/* Display suppliers when detailed data is available */}
                {detailedData && !loadingDetails && (
                  <>
                    <Row>
                      <Col>
                        <p className="fs-6 fw-bold">Suppliers: </p>
                      </Col>
                    </Row>

                    {/* Supplier details */}
                    <Table striped bordered hover>
                      <thead>
                        <tr className="text-center">
                          <th>#</th>
                          <th>Name</th>
                          <th>Website</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailedData.suppliers?.length ? (
                          <>
                            {detailedData.suppliers.map((supplier, index) => (
                              <React.Fragment key={index}>
                                <tr className="text-center italic-text">
                                  <td>{index + 1}</td>
                                  <td>
                                    {/* Supplier detail modal */}
                                    <SupplierDetailModal
                                      supplierObj={supplier}
                                      supplierId={supplier.id}
                                    />
                                  </td>
                                  <td>
                                    {/* Supplier website link */}
                                    <a
                                      href={supplier.website}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      <NavigateToUrlIcon />
                                    </a>
                                  </td>
                                </tr>
                              </React.Fragment>
                            ))}
                          </>
                        ) : (
                          <tr>
                            <td colSpan="3" className="text-center">
                              No suppliers found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>

                    {/* Display products */}
                    <Row>
                      <Col>
                        <p className="fs-6 fw-bold">Products: </p>
                      </Col>
                    </Row>

                    {/* Product details */}
                    <Table striped bordered hover>
                      <thead>
                        <tr className="text-center">
                          <th>#</th>
                          <th>Name</th>
                          <th>Catalogue Number</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailedData.products?.length ? (
                          <>
                            {detailedData.products.map((product, index) => (
                              <React.Fragment key={index}>
                                <tr className="text-center italic-text">
                                  <td>{index + 1}</td>
                                  <td>
                                    {/* Product detail modal */}
                                    <ProductDetailModal
                                      productObj={product}
                                      productId={product.id}
                                    />
                                  </td>
                                  <td>{product.cat_num}</td>
                                </tr>
                              </React.Fragment>
                            ))}
                          </>
                        ) : (
                          <tr>
                            <td colSpan="3" className="text-center">
                              No products found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </>
                )}

                {/* Fallback message when no detailed data is available */}
                {!detailedData && !loadingDetails && !error && (
                  <Row>
                    <Col>
                      <div className="alert alert-info" role="alert">
                        Click to load detailed information about suppliers and
                        products.
                      </div>
                    </Col>
                  </Row>
                )}
              </Container>
            )}
          </Modal.Body>
          {manufacturer && (
            <Modal.Footer className="d-flex flex-row justify-content-between">
              {!isSupplier && !isViewer && (
                <div className="d-flex flex-row">
                  <div className="me-2">
                    {/* Manufacturer edit modal */}
                    <ManufacturerModal
                      manufacturerObj={manufacturer}
                      clearSearchValue={clearSearchValue}
                    />
                  </div>

                  {/* Manufacturer delete button */}
                  <DeleteButton
                    objectType="manufacturer"
                    objectName={manufacturer.name}
                    objectId={manufacturer.id}
                    deleteFetchFunc={deleteManufacturer}
                    onSuccessfulDelete={handleDelete}
                    clearSearchValue={clearSearchValue}
                  />
                </div>
              )}

              {/* Close button */}
              <Button variant="secondary" onClick={handleClose}>
                Close
              </Button>
            </Modal.Footer>
          )}
        </Modal>
      )}
    </div>
  );
};
export default ManufacturerDetailModal;
